import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ChartBar, TrendUp } from '@phosphor-icons/react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  charges_fixes: '#002FA7',
  masse_salariale: '#2E7D32',
  carburant: '#EF6C00',
  depenses_variables: '#9C27B0',
};

const LABELS = {
  charges_fixes: 'Charges fixes',
  masse_salariale: 'Masse salariale',
  carburant: 'Carburant',
  depenses_variables: 'Depenses variables',
};

const fmtEUR = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} EUR`;
const fmtMonth = (m) => {
  try {
    const d = new Date(m + '-01');
    return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  } catch { return m; }
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: '6px', padding: '10px 12px', fontSize: '0.85rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: '700', marginBottom: '6px' }}>{fmtMonth(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', color: p.color }}>
          <span>{LABELS[p.dataKey]}</span>
          <strong>{fmtEUR(p.value)}</strong>
        </div>
      ))}
      <div style={{ borderTop: '1px solid #E2E2E6', marginTop: '6px', paddingTop: '6px', display: 'flex', justifyContent: 'space-between', gap: '16px', fontWeight: '700' }}>
        <span>Total</span>
        <span>{fmtEUR(total)}</span>
      </div>
    </div>
  );
};

export const MonthlyCostsWidget = ({ wrapStyle }) => {
  const [data, setData] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/charges/monthly-costs`, { params: { months: 12 }, withCredentials: true });
      setData(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (!data) return null;

  return (
    <div style={wrapStyle}>
      <div className="card" data-testid="monthly-costs-widget">
        <div className="card-header" style={{ backgroundColor: '#E8EAF6' }}>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#002FA7' }}>
            <ChartBar size={20} weight="bold" /> Couts mensuels - 12 derniers mois
          </h3>
        </div>
        <div style={{ padding: '16px 12px 8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', padding: '0 8px' }}>
            <div style={{ fontSize: '0.85rem', color: '#5E5F66' }}>Total annuel : <strong style={{ color: '#002FA7' }}>{fmtEUR(data.total)}</strong></div>
            <div style={{ fontSize: '0.85rem', color: '#5E5F66', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <TrendUp size={14} /> Moyenne/mois : <strong style={{ color: '#002FA7' }}>{fmtEUR(data.avg_month)}</strong>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.months} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E2E6" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '0.8rem' }} formatter={(v) => LABELS[v] || v} />
              <Bar dataKey="charges_fixes" stackId="a" fill={COLORS.charges_fixes} />
              <Bar dataKey="masse_salariale" stackId="a" fill={COLORS.masse_salariale} />
              <Bar dataKey="carburant" stackId="a" fill={COLORS.carburant} />
              <Bar dataKey="depenses_variables" stackId="a" fill={COLORS.depenses_variables} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
